import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Topbar } from '@components/topbar'
import { Footer } from '@components/footer'
import { SEO } from '@components/seo'
import { useStoreActions, useStoreState } from '@stores'
import { CLIENT_CODE, SITE_URL } from '@constants'

const formatPromoDate = (start?: string, end?: string): string => {
	if (!start && !end) return ''
	const fmt = (s?: string) => {
		if (!s) return ''
		const d = new Date(s)
		if (isNaN(d.getTime())) return s
		return d.toLocaleDateString('en-GB', {
			day: 'numeric',
			month: 'short',
			year: 'numeric'
		})
	}
	if (start && end) return `${fmt(start)} – ${fmt(end)}`
	return fmt(start || end)
}

export const PromotionPage = () => {
	const { fetchAllPromo } = useStoreActions(action => action.promo)
	const { promos } = useStoreState(state => state.promo)

	useEffect(() => {
		fetchAllPromo({
			page: 1,
			limit: 100,
			client_code: CLIENT_CODE,
			status: 'all'
		})
	}, [fetchAllPromo])

	const promoListJsonLd = {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: 'Promotions — Botani Square Mall',
		url: `${SITE_URL}/promotion`,
		numberOfItems: (promos || []).length,
		itemListElement: (promos || []).slice(0, 50).map((p, i) => ({
			'@type': 'ListItem',
			position: i + 1,
			url: `${SITE_URL}/promotion/${p.slug}`,
			name: p.name
		}))
	}

	const breadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
			{
				'@type': 'ListItem',
				position: 2,
				name: 'Promotions',
				item: `${SITE_URL}/promotion`
			}
		]
	}

	return (
		<div className="min-h-screen bg-gray-50">
			<SEO
				title="Promotions & Special Offers"
				description="Discover the latest promotions, member treats, and exclusive deals from tenants at Botani Square Mall Bogor."
				path="/promotion"
				keywords={[
					'promo Botani Square',
					'promo mall Bogor',
					'diskon Bogor',
					'member treat Pundis'
				]}
				jsonLd={[promoListJsonLd, breadcrumb]}
			/>
			<Topbar />

			{/* Hero Section with Banner Image */}
			<section className="relative pt-28 pb-10 sm:pt-32 sm:pb-14 overflow-hidden">
				{/* Background Banner Image */}
				<div className="absolute inset-0">
					<img
						src={require('@assets/images/banner-2.png')}
						alt="Promotion Banner"
						className="w-full h-full object-cover"
					/>
					{/* Overlay for better text readability */}
					<div className="absolute inset-0 bg-black bg-opacity-50"></div>
				</div>
				<div className="relative max-w-screen-xl mx-auto px-6 text-center">
					<h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-3">
						Exclusive Promotions
					</h1>
					<p className="text-sm sm:text-base text-white max-w-2xl mx-auto leading-relaxed">
						Don't miss out on incredible deals and limited-time offers from your
						favorite stores at Botani Square.
					</p>
				</div>
			</section>

			{/* Promotion Cards Section */}
			<section className="py-12 sm:py-16 md:py-20 bg-white">
				<div className="max-w-screen-xl mx-auto px-4 sm:px-6">
					{promos && promos.length > 0 ? (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
							{promos.map((promo, index) => {
								const dateLabel = formatPromoDate(
									promo.period?.start,
									promo.period?.end
								)
								return (
									<Link
										key={promo.id || index}
										to={`/promotion/${promo.slug}`}
										className="group flex flex-col bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
									>
										<div className="relative aspect-[2/1] bg-gradient-to-br from-blue-100 to-indigo-100 overflow-hidden">
											{promo.photos && promo.photos.length > 0 ? (
												<img
													src={promo.photos[0]}
													alt={promo.name || 'Promo Image'}
													className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
												/>
											) : (
												<div className="absolute inset-0 flex items-center justify-center text-4xl">
													🎉
												</div>
											)}
										</div>
										<div className="flex flex-col flex-1 p-5 sm:p-6">
											{dateLabel && (
												<div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
													{dateLabel}
												</div>
											)}
											<h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 line-clamp-2 flex-1">
												{promo.name || 'Promo Title'}
											</h3>
											<span className="inline-flex items-center gap-1 text-primary font-medium text-sm mt-auto group-hover:underline">
												View details
												<span aria-hidden>→</span>
											</span>
										</div>
									</Link>
								)
							})}
						</div>
					) : (
						<div className="text-center py-16">
							<div className="text-6xl mb-4">🎉</div>
							<h3 className="text-2xl font-bold text-gray-800 mb-4">
								No Promotions Available
							</h3>
							<p className="text-gray-600 text-lg">
								Check back later for exciting promotions!
							</p>
						</div>
					)}
				</div>
			</section>

			{/* Newsletter Section */}
			{/* <section className="py-16 sm:py-20 bg-gray-800">
				<div className="max-w-screen-xl mx-auto px-6 text-center">
					<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
						Never Miss a Deal
					</h2>
					<p className="text-lg sm:text-xl text-white mb-6 sm:mb-8 max-w-2xl mx-auto">
						Subscribe to our newsletter and be the first to know about exclusive
						promotions and flash sales.
					</p>

					<div className="flex flex-col sm:flex-row max-w-md mx-auto">
						<input
							type="email"
							placeholder="Enter your email address"
							className="flex-1 px-6 py-3 rounded-l-lg sm:rounded-l-lg sm:rounded-r-none border-0 focus:outline-none focus:ring-2 focus:ring-primary text-gray-800"
						/>
						<button className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-r-lg sm:rounded-r-lg sm:rounded-l-none font-medium transition-colors duration-200">
							Subscribe
						</button>
					</div>
				</div>
			</section> */}

			{/* Footer */}
			<Footer />
		</div>
	)
}
